import { Pagination, Popconfirm, Space, TableColumnsType, Tabs, Typography } from "antd";
import { ContentWrapper, DataTable, PDFViewer } from "~/Components";

import { useNotification } from "@hooks/useNotification";
import { Icon } from "@iconify/react";
import { debounce, get } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "~/Hooks/useAuth";
import { useQuery } from "~/Hooks/useQuery";
import { magpBEApi } from "~/Services";
import { getFormattedDate } from "~/Utils/CommonUtils";

export const MAGPAccessManagement = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [selectedDocument, setSelectedDocument] = useState<string>("");
  const [searchString, setSearchString] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderByName, setOrderByName] = useState("requestedAt");
  const { openToast } = useNotification();

  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    let status = "new";
    let activeTab = query.get("activeTab");
    if (activeTab === "pendingUsers") status = "new";
    else if (activeTab === "activeUsers") status = "active";
    else if (activeTab === "deactivatedUsers") status = "deactive";
    else if (activeTab === "rejectedUsers") status = "reject";
    setIsLoading(true);
    let url = `/api/login/signup/vms/users/list?status=${status}&page=${currentPage}&size=${recordsPerPage}&sortField=${orderByParam}&sortOrder=${sortOrder}&searchText=${searchString}`;
    magpBEApi
      // .get(url, {
      //   headers: {
      //     Authorization: `Bearer ${user.token}`,
      //   },
      // })
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let result = get(res, "data.data");
          setTotalRecords(get(result, "totalElements", 0));
          let temp = get(result, "users", []);
          temp.map((item: any, index: number) => {
            return {
              ...item,
              key: index,
            };
          });
          setData(temp);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const viewDocument = async (url: any, record: any = {}, isDownload = false) => {
    // if (loadingDocId) return;
    // setLoadingDocId(envelope.documentId);
    if (isDownload) {
      try {
        const response = await fetch(url, {
          method: "GET",
        });
        const downloadedData = await response.blob();
        const filename = `MAGP_${record.email}_receipt.pdf`;
        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(downloadedData);
        downloadLink.download = filename;
        downloadLink.style.display = "none";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadLink.href);
      } catch (error) {}
      // setLoadingDocId("");
      return;
    }
    setSelectedDocument(url);
    // setLoadingDocId("");
    return;
  };

  const updateUserStatus = async (record: any, type = "activate") => {
    let status: string = "ACTIVATED";
    let active: boolean = true;
    let statusText: string = "activated";
    if (type === "reject") {
      active = false;
      status = "REJECTED";
      statusText = "rejected";
    } else if (type === "deactivate") {
      active = false;
      status = "DEACTIVATED";
      statusText = "deactivated";
    }
    const request = await magpBEApi.post(`/api/login/signup/vms/activate-deactivate`, {
      approvalId: record.email,
      approverId: user.email,
      active,
      status,
    });
    let { data } = request;
    if (data.code === "ACCEPTED") {
      openToast({
        content: `${record.userName} has been ${statusText} successfully`,
        type: "success",
      });
      getRecords(sortOrder, orderByName, searchString, 0);
    } else {
      openToast({
        content: data.message || "something went wrong",
        type: "error",
      });
    }
  };

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, page);
  }, [page, query, searchString]);

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/dedicated/magp/access-management?activeTab=pendingUsers`, {
        replace: true,
      });
    }
  }, []);

  const pendingColumns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },
    {
      title: "Proof of Payment",
      dataIndex: "pdf",
      render: (value: any, record: any) => (
        <Space>
          <Icon
            icon="ic:outline-remove-red-eye"
            inline
            fontSize={24}
            className="doc-icon-secondary"
            cursor="pointer"
            style={{ marginLeft: 16, marginRight: 8 }}
            onClick={() => viewDocument(value)}
          />
          {
            <Icon
              icon="material-symbols:download"
              inline
              className="doc-icon-secondary"
              fontSize={24}
              style={{ marginLeft: 8, marginRight: 8 }}
              cursor="pointer"
              onClick={() => viewDocument(value, record, true)}
            />
          }
        </Space>
      ),
      sorter: false,
    },
    {
      title: "Requested At",
      dataIndex: "requestedAt",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "requestedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "requestedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Action",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Popconfirm
            title={`Are you sure to Approve ${record.userName}?`}
            onConfirm={() => updateUserStatus(record, "activate")}
            okText={"Yes"}
            cancelText={"Cancel"}
          >
            <Icon
              height={30}
              icon={"ic:twotone-download-done"}
            />
          </Popconfirm>
          <Popconfirm
            title={`Are you sure to Reject ${record.userName}?`}
            onConfirm={() => updateUserStatus(record, "reject")}
            okText={"Yes"}
            cancelText={"Cancel"}
          >
            <Icon
              height={30}
              icon={"ic:outline-cancel"}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const activeColumns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },
    {
      title: "Proof of Payment",
      dataIndex: "pdf",
      render: (value: any, record: any) => (
        <Space>
          <Icon
            icon="ic:outline-remove-red-eye"
            inline
            fontSize={24}
            className="doc-icon-secondary"
            cursor="pointer"
            style={{ marginLeft: 16, marginRight: 8 }}
            onClick={() => viewDocument(value)}
          />
          {
            <Icon
              icon="material-symbols:download"
              inline
              className="doc-icon-secondary"
              fontSize={24}
              style={{ marginLeft: 8, marginRight: 8 }}
              cursor="pointer"
              onClick={() => viewDocument(value, record, true)}
            />
          }
        </Space>
      ),
      sorter: false,
    },
    {
      title: "Activated At",
      dataIndex: "activatedAt",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "activatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "activatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Action",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Popconfirm
            title={`Are you sure to Deactivate ${record.userName}?`}
            onConfirm={() => updateUserStatus(record, "deactivate")}
            okText={"Yes"}
            cancelText={"Cancel"}
          >
            <Icon
              height={30}
              icon={"ant-design:lock-filled"}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deactivatedColumns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },
    {
      title: "Proof of Payment",
      dataIndex: "pdf",
      render: (value: any, record: any) => (
        <Space>
          <Icon
            icon="ic:outline-remove-red-eye"
            inline
            fontSize={24}
            className="doc-icon-secondary"
            cursor="pointer"
            style={{ marginLeft: 16, marginRight: 8 }}
            onClick={() => viewDocument(value)}
          />
          {
            <Icon
              icon="material-symbols:download"
              inline
              className="doc-icon-secondary"
              fontSize={24}
              style={{ marginLeft: 8, marginRight: 8 }}
              cursor="pointer"
              onClick={() => viewDocument(value, record, true)}
            />
          }
        </Space>
      ),
      sorter: false,
    },
    {
      title: "Deactivated At",
      dataIndex: "deActivatedAt",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "deActivatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "deActivatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Action",
      dataIndex: "user",
      render: (value: any, record: any) => (
        <div style={{ display: "flex", gap: 10 }}>
          <Popconfirm
            title={`Are you sure to Reactivate ${record.userName}?`}
            onConfirm={() => updateUserStatus(record, "activate")}
            okText={"Yes"}
            cancelText={"Cancel"}
          >
            <Icon
              height={30}
              icon={"pajamas:lock-open"}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const rejectedColumns: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "userName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "userName"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "email"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "email"
            ? "descend"
            : undefined,
    },
    {
      title: "Proof of Payment",
      dataIndex: "pdf",
      render: (value: any, record: any) => (
        <Space>
          <Icon
            icon="ic:outline-remove-red-eye"
            inline
            fontSize={24}
            className="doc-icon-secondary"
            cursor="pointer"
            style={{ marginLeft: 16, marginRight: 8 }}
            onClick={() => viewDocument(value)}
          />
          {
            <Icon
              icon="material-symbols:download"
              inline
              className="doc-icon-secondary"
              fontSize={24}
              style={{ marginLeft: 8, marginRight: 8 }}
              cursor="pointer"
              onClick={() => viewDocument(value, record, true)}
            />
          }
        </Space>
      ),
      sorter: false,
    },
    {
      title: "Rejected At",
      dataIndex: "requestedAt",
      render: (value: any, record: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sortOrder:
        sortOrder === "asc" && orderByName === "requestedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "requestedAt"
            ? "descend"
            : undefined,
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getRecords(order, column.dataIndex, searchString, page);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  const items = [
    {
      label: "Pending Users",
      key: "pendingUsers",
      children: (
        <>
          <DataTable
            columns={pendingColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search"}
            hideExport={true}
            showTopBar={true}
            inputDisable={isLoading}
            isServerSearchApi={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(curPage) => {
                setPage(curPage - 1);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Active Users",
      key: "activeUsers",
      children: (
        <>
          <DataTable
            columns={activeColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search"}
            hideExport={true}
            showTopBar={true}
            inputDisable={isLoading}
            isServerSearchApi={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(curPage) => {
                setPage(curPage - 1);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Deactivated Users",
      key: "deactivatedUsers",
      children: (
        <>
          <DataTable
            columns={deactivatedColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search"}
            hideExport={true}
            showTopBar={true}
            inputDisable={isLoading}
            isServerSearchApi={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(curPage) => {
                setPage(curPage - 1);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Rejected Users",
      key: "rejectedUsers",
      children: (
        <>
          <DataTable
            columns={rejectedColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={data}
            className="table-clickable"
            searchPlaceholderText={"Search"}
            hideExport={true}
            showTopBar={true}
            inputDisable={isLoading}
            isServerSearchApi={true}
            onSearchChange={handleSearchChange}
            multiSelect={false}
            isPagination={true}
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(curPage) => {
                setPage(curPage - 1);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <ContentWrapper title={"MAGP Access Management"}>
      {selectedDocument && (
        <PDFViewer
          show={true}
          title={"Proof of Payment"}
          subTitle={""}
          setShow={() => setSelectedDocument("")}
          url={selectedDocument}
        />
      )}
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          setPage(0);
          navigate(`/dedicated/magp/access-management?activeTab=${activeKey}`);
        }}
      ></Tabs>
    </ContentWrapper>
  );
};
